import * as React from "react";
import { ConnectDragPreview, ConnectDragSource, DragSource, DragSourceMonitor } from "react-dnd";
import { FaTrash, FaBars } from "react-icons/fa";
import styled from "styled-components";

import { typeTitle } from "../../../../../formatters/courseAuthor";
import colors from "../../../../../styles/colors";
import { fontSizeNumbers } from "../../../../../styles/fontSizes";
import { courseComponentViews, courseInstructionViews } from "../../../../../utils/courseViews";
import { PreferredLocale } from "../../../../../utils/locales";
import { CourseUnitView, ICourseInstruction } from "../../../../course-author/types";
import ActionConfirmationButton, { InstructionActionButtonContainer } from "../../ActionConfirmation";
import EditableTitleLink from "../../EditableTitleLink";
import { DraggableItem } from "../DraggableContainer.styles";
import { IDropItem } from "../DropTargetFactory";
import { DragAndDropTypes } from "../types";
import { ICourseEditOperations } from "../../../../course-author/types";
import { DropZone, useDragAndDrop } from "../../../../../context/UseDragAndDrop";
import { TooltipWrapper } from "../../../../pages/Analytics/FilterCriteria/FilterCriteria";
import CloneArrows from "../../../../utils/Icons/CloneArrows";
import { onHandleChildDragStart } from "../../../../../utils/dragAndDrop";
import { useTabIdContext } from "../../../../../context/TabIdContext";

export const InstructionalItemRow = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  height: 3rem;
  padding: 0.5rem;
  width: calc(100% - 1.25rem);
  z-index: 0;
  justify-content: space-between;
  &:hover {
    background-color: ${colors.lightblue};
  }
`;

const dragSourceStyle = {
  alignItems: "center",
  cursor: "move",
  display: "flex",
  height: "3rem",
  justifyContent: "center",
  width: "3rem",
};

export interface IInstructionalItemOwnProps {
  addTitle: (title: string) => void;
  courseUnit: ICourseInstruction;
  deleteItem: () => void;
  dropPosition: number;
  editOperations: ICourseEditOperations;
  item: ICourseInstruction;
  locale: PreferredLocale;
  maxTitleWidth: number;
  modifyTitle: (title: string) => void;
  navigate: () => void;
  unitPath: CourseUnitView[];
  url: string;
  bgColor?: string;
}

const DraggableInstruction = styled(DraggableItem).attrs(props => ({
  // tslint:disable-next-line: no-string-literal
  background: props["background"],
}))`
  background: ${props => (props.isDragging ? "" : props.background)};
`;

export interface IInstructionalItemCollectedProps {
  connectDragPreview: ConnectDragPreview;
  connectDragSource: ConnectDragSource;
  isDraggingReact: boolean;
}

export type IInstructionalItemProps = IInstructionalItemOwnProps & IInstructionalItemCollectedProps;

const InstructionalItem: React.FC<IInstructionalItemProps> = props => {
  const { bgColor, connectDragPreview, connectDragSource, isDraggingReact, locale, item, editOperations } = props;

  const { handleDragging } = useDragAndDrop();

  const { tabId: sourceTabId } = useTabIdContext();

  const handleDragStart = onHandleChildDragStart(
    item,
    "draggable-instruction",
    "instruction",
    handleDragging,
    sourceTabId
  );

  const handleDragEnd = () => handleDragging(false);

  return (
    <>
      {connectDragPreview(
        <div>
          <DraggableInstruction id="draggable-instruction" isDragging={isDraggingReact} background={bgColor}>
            <DropZone
              dropPosition={props.dropPosition}
              dropZoneType={"instruction"}
              newInstructionType={item.content.type}
              editOperations={editOperations}
              path={props.unitPath}
            >
              <InstructionalItemRow style={isDraggingReact ? { visibility: "hidden" } : {}}>
                <EditableTitleLink
                  addTitle={props.addTitle}
                  currentTitle={courseComponentViews.localizedTitle(locale)(item)}
                  defaultTitle={typeTitle(courseInstructionViews.type(item))}
                  fontSize={fontSizeNumbers.small}
                  maxWidth={props.maxTitleWidth}
                  modifyTitle={props.modifyTitle}
                  navigate={props.navigate}
                  to={props.url}
                  isMainTitle={false}
                />
                <InstructionActionButtonContainer>
                  <ActionConfirmationButton action={"Delete"} actionFn={props.deleteItem} ActionIcon={FaTrash} />
                  {connectDragSource(
                    <div>
                      <TooltipWrapper id="reorder-slides" content="Reorder Slides" containerStyle={dragSourceStyle}>
                        <FaBars />
                      </TooltipWrapper>
                    </div>
                  )}
                  <div draggable={true} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                    <TooltipWrapper id="dnd-slides" content="Clone across tabs" containerStyle={dragSourceStyle}>
                      <CloneArrows />
                    </TooltipWrapper>
                  </div>
                </InstructionActionButtonContainer>
              </InstructionalItemRow>
            </DropZone>
          </DraggableInstruction>
        </div>
      )}
    </>
  );
};

const listItemSource = {
  beginDrag(props: IInstructionalItemProps, monitor: DragSourceMonitor): IDropItem {
    return {
      dropPosition: props.dropPosition,
      item: props.item,
      path: props.unitPath,
      type: courseInstructionViews.type(props.item),
    };
  },
};

export default DragSource<IInstructionalItemOwnProps, IInstructionalItemCollectedProps>(
  DragAndDropTypes.InstructionListItem,
  listItemSource,
  (connect, monitor) => ({
    connectDragPreview: connect.dragPreview(),
    connectDragSource: connect.dragSource(),
    isDraggingReact: monitor.isDragging(),
  })
)(InstructionalItem);
